import { Auth } from '../../Common/lib/auth';
import React, { useState, useEffect, useRef} from "react";
import { getUserSession } from "../../Common/lib/Session";
import { s3Upload } from "../../Common/lib/Session";
import Authorized from "../Authorized/index.tsx";
import FlakeID from "flake-idgen";
import intformat from "biguint-format";
import { API } from "../../Common/lib/api";
import { populateLocalStorage } from "../../Common/filtersLocalStorage"

import ProjectDescription from "./projectDescription";
import ProjectContributorsInformation from "./projectContributorsInformation";
import LicensingInformation from "./licensingInformation";
import SubmitProject from "./submitProject";
import FileAttachment from "./fileAttachment";
import Acknowledgement from "./acknowledgement";
import useDocumentTitle from '../../Common/useDocumentTitle';

let keywordIDs = [];
let vesselIDs = [];

export default function SubmitData() {

  const forceSignOutRef = useRef(false);
  const [uploadProgress, setUploadProgress] = useState({
    loaded: 0,
    total: 100,
  });
  const [projectUploadLoading, setProjectUploadLoading] = useState(true);
  const [projectUploadFail, setProjectUploadFail] = useState(false);

  const [step, setStep] = useState(1);
  const emptyProjectObject = {
    selectedOption: null,
    selectedStartDate: "",
    selectedEndDate: "",
    title: "",
    abstract: "",
    researchName: "",
    website: "",
    vocabulary: "",
    keywordUrl: "",
    researchType: [],
    bounds: {
      north: "",
      south: "",
      east: "",
      west: "",
    },
    coordinates: {
      northLatitude: "",
      southLatitude: "",
      eastLongitude: "",
      westLongitude: "",
    },
    centerPoint: [],
    showCoordinates: false,
    doi: "",
    citation: "",
    vessels: [],
    keyword: [],
    restrictedData: false,
    analyzedForNAVWARN: false,
    backscatterAvailable: false,
    soundingHardwareModel: "",
    techniqueOfSounding: "",
    dataAcquisitionSoftwareVersion: "",
    dataProcessingSoftwareVersion: "",
    horizontalCoordinatesSystem: "",
    horizontalDatum: "",
    projection: "",
    positioningMethod: "",
    positioningHardware: "",
    benchmarkReference: "",
    benchmarkCoordinates: "",
    verticalReferenceSystem: "",
    benchmarkReferenceHeight: "",
    waterLevelReductionMethodFileApplied: "",
    tidalStationReferenceVerticalReductionModel: "",
    horizontalAccuracy: "",
    verticalAccuracy: "",
    soundingCorrectedForVesselDraft: false,
    calibrationData: false,
    IHO_CATZOC: "",
    IHOOrderOfSurvey: "",
    resolution: "",

    //State for ProjectContributorsInformation
    publisherName: "",
    piName: "",
    piEmail: "",
    piAffiliation: "",
    inputPiAffiliation: "",
    inputPiRole: "",
    piORCID: "",
    piRole: "Principal Investigator",
    piAdd: "",
    piCity: "",
    piProvince: "",
    tempPostalCode: "",
    piPostalCode: "",
    piCountry: "Canada",
    tempPhone: "",
    piPhone: "",
    contributorList: [{}],

    //State for LicensingInformation
    selectedLicense: "CC BY-NC-SA 4.0",
    notOpenlicenseName: "",
    openlicenseName: "",
    sponsorList: [{}],

    //State for ProjectVisibilityInformation and file attachment
    files: [],
    keyFiles: [],
    dataPolicy: "Public", // Used to set files to private/public
    isPublic: true,
    TermOfAccess: false,
    TermsOfConditions: false,
    ocapCareDeclaration: false,
    errors: false,
    message: [],

    keywordChips: [],
    researchTypeChips: [],
    vesselChips: [],
  }
  const [projectData, setProjectData] = useState(emptyProjectObject);

  useDocumentTitle("Submit Data");

  useEffect(() => {
    populateLocalStorage();
    // Initialize publisher name from session
    getUserSession().then((session) => {
      if (session && session.fname && session.lname) {
        setProjectData((prevState) => ({
          ...prevState,
          publisherName: `${session.fname} ${session.lname}`.trim()
        }));
      }
    });
  }, []);

// Prompts user to confirm page refresh
useEffect(() => {
    window.onbeforeunload = function () {
        return true;
    };

    return () => {
        window.onbeforeunload = null;
    };
}, []);



  /**
   * Retrieves an array of attribute objects that are used for filters (keywords, vessels, publishers).
   * @param {*} attributeName Name of the category of attributes you want to retrieve from local storage. Ex: keyword, vessel, publisher
   * @returns Array of attribute objects
   */
  function getAttributesByName(attributeName) {
    let attributeChoices = [];
    // Check if attributes and attributes.filterOptions exist in localStorage before trying to populate attributeChoices.
    try {
      const attributesStr = localStorage.getItem("attributes");
      if (attributesStr) {
        const attributesData = JSON.parse(attributesStr);
        if (attributesData && attributesData.filterOptions && Array.isArray(attributesData.filterOptions)) {
          attributeChoices = attributesData.filterOptions.filter((filterOption) => filterOption.category === attributeName);
          attributeChoices = attributeChoices.filter((value, index, self) => self.findIndex((value2) => value2.name.toLowerCase() === value.name.toLowerCase()) === index); // Removes duplicate objects based on name
        }
      }
    } catch (e) {
      console.error('Error parsing attributes from localStorage:', e);
    }
    return attributeChoices;
  }

  // PROJECT UPLOAD START #############################################################################################################################################
  // Used to create a 19 digit ID to append to PK's and SK's of projects and attributes.
  function flakeID() {
    const flakeID = new FlakeID();
    return intformat(flakeID.next(), "dec");
  };

  // Handles the file uploading within the s3 bucket.
  async function handleUploadFile() {
    let attachmentData = [];
    if (projectData.files.length > 0) {
      let promises = projectData.files.map(async (file) => {
        let fileId = await s3Upload(file.fileData, setUploadProgress, setProjectUploadFail);
        return {
            key: fileId,
            description: file.filedescription
        };
      });
      attachmentData = await Promise.all(promises);
    }
    return attachmentData;
  }




  /**
   * Converts a list of numbers of bytes to the appropriate size format and adds a suffix (KB, MB, GB, TB).
   * @returns Converted numbers
   */
  function getFileSizes() {
    let fileSizes = [];

    if (projectData.files.length > 0) {

      projectData.files.forEach(file => {
        const bytes = file.fileData.size;

        if (bytes === 0) {
          fileSizes.push('0 Bytes')
        }
        else {
          const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
          const i = Math.floor(Math.log(bytes) / Math.log(1024));
          fileSizes.push(parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i]);
        }
      })
    }

    return fileSizes
  }

  // Handles the creation of a project.
// File: submitPC.js

async function createProjects() {
  try {
    setProjectUploadFail(false);
    setProjectUploadLoading(true);
    await handleCreatePublisher();
    await handleCreateKeywords();
    await handleCreateVessels();
    // Refresh localStorage after creating new attributes
    await populateLocalStorage();
    const fundingReference = handleCreateFundingReference();
    let attachmentData = await handleUploadFile();
    let fileSizes = getFileSizes();

    let session = await getUserSession();

    let publisher = {
      name: projectData.publisherName || (session.fname + " " + session.lname),
      role: "Publisher",
    }

    let sponsors = "";

    if (Object.keys(projectData.sponsorList[0]).length) {
      sponsors = JSON.stringify(projectData.sponsorList);
    }

    let principalInvestigator = {
      name: projectData.piName,
      role: projectData.piRole,
      email: projectData.piEmail,
      ORCID: projectData.piORCID,
      affiliation: projectData.piAffiliation,
      inputRole: projectData.inputPiRole,
      inputAffiliation: projectData.inputPiAffiliation,
      phone: projectData.piPhone,
      city: projectData.piCity,
      country: projectData.piCountry,
      postalCode: projectData.piPostalCode,
      province: projectData.piProvince,
      address: projectData.piAdd,
    }

    const getUTCAdjustedDate = (dateString) => {
      const dateObj = new Date(dateString);
      if (isNaN(dateObj.getTime())) return null; // Return null for invalid dates
      return dateObj.getTime() + dateObj.getTimezoneOffset() * 60000;
    };

    const endDate = getUTCAdjustedDate(projectData.selectedEndDate);
    const startDate = getUTCAdjustedDate(projectData.selectedStartDate);
    const projectIdentifier = `project-${flakeID()}`;

    // Creates a project within the Projects table.
    await API.projects.create({
      PK: projectIdentifier,
      SK: projectIdentifier,
      abstract: projectData.abstract,
      purpose: projectData.purpose,
      citation: projectData.citation,
      DOI: projectData.doi,
      eastLongitude: projectData.bounds.east,
      endDate: endDate,
      keywords: JSON.stringify(projectData.keyword),
      license: JSON.stringify(projectData.selectedLicense),
      northLatitude: projectData.bounds.north,
      researchProgramName: projectData.researchName,
      southLatitude: projectData.bounds.south,
      sponsors: sponsors,
      stakeholders: JSON.stringify([publisher, principalInvestigator, ...projectData.contributorList.slice(1)]),
      publisher: projectData.publisherName || publisher.name,
      startDate: startDate,
      status: projectData.selectedOption.value,
      title: projectData.title,
      vessels: JSON.stringify(projectData.vessels),
      website: projectData.website,
      westLongitude: projectData.bounds.west,
      attachmentKeys: JSON.stringify(attachmentData),
      fileSizes: JSON.stringify(fileSizes),
      publishStatus: "under review",
      centerPoint: JSON.stringify(projectData.centerPoint),
      analyzedForNAVWARN: projectData.analyzedForNAVWARN,
      backscatterAvailable: projectData.backscatterAvailable,
      soundingHardwareModel: projectData.soundingHardwareModel,
      techniqueOfSounding: projectData.techniqueOfSounding,
      resolution: projectData.resolution,
      dataAcquisitionSoftwareVersion: projectData.dataAcquisitionSoftwareVersion,
      dataProcessingSoftwareVersion: projectData.dataProcessingSoftwareVersion,
      horizontalCoordinatesSystem: projectData.horizontalCoordinatesSystem,
      horizontalDatum: projectData.horizontalDatum,
      projection: projectData.projection,
      positioningMethod: projectData.positioningMethod,
      positioningHardware: projectData.positioningHardware,
      benchmarkReference: projectData.benchmarkReference,
      benchmarkCoordinates: projectData.benchmarkCoordinates,
      verticalReferenceSystem: projectData.verticalReferenceSystem,
      benchmarkReferenceHeight: projectData.benchmarkReferenceHeight,
      waterLevelReductionMethodFileApplied: projectData.waterLevelReductionMethodFileApplied,
      tidalStationReferenceVerticalReductionModel: projectData.tidalStationReferenceVerticalReductionModel,
      horizontalAccuracy: projectData.horizontalAccuracy,
      verticalAccuracy: projectData.verticalAccuracy,
      soundingCorrectedForVesselDraft: projectData.soundingCorrectedForVesselDraft,
      calibrationData: projectData.calibrationData || "",
      IHO_CATZOC: projectData.IHO_CATZOC,
      IHOOrderOfSurvey: projectData.IHOOrderOfSurvey,
      ocapCareDeclaration: projectData.ocapCareDeclaration,
    });
    setProjectUploadLoading(false);
  } catch (e) {
    setProjectUploadLoading(false);
    setProjectUploadFail(true);
  }
};


  // Handles the creation of a publisher within the Attributes table.
  async function handleCreatePublisher() {
    // publisherChoices contains all of the publishers from the database and is used to check existing publishers before creating a new publisher record in database
    let publisherChoices = getAttributesByName("publisher");
    publisherChoices = publisherChoices.map(obj => obj.name); // Formats publisherChoices to get rid of object properties and just leave the names of the publishers in the array

    try {
      let publisherID = flakeID();
      let publisher = {};
      publisher.name = "";

      let sessionPromise = getUserSession();
      await sessionPromise.then(async (res) => {
        publisher.name = res.fname + " " + res.lname;
        // Check if publisher alreay exists in database before uploading
        if (!publisherChoices.includes(publisher.name)) {
          return await API.attributes.create({
            PK: "publisher-" + res.username,
            category: "publisher",
            name: publisher.name,
          });
        }
      })
    } catch (error) {
      // console.log(error.message);
    }
  };

  // Handles creating keywords in the Attributes table.
  async function handleCreateKeywords() {
    // keywordChoices contains all of the keywords from the database and is used to check existing publishers before creating a new publisher record in database
    let keywordChoices = getAttributesByName("keyword");
    keywordChoices = keywordChoices.map(obj => obj.name); // Formats keywordChoices to get rid of object properties and just leave the names of the keywords in the array

    let filteredProjectKeywords = projectData.keyword.slice();
    filteredProjectKeywords = filteredProjectKeywords.filter(item => !keywordChoices.includes(item)); // Checks project keywords and filters out any keywords that already exist in the database

    try {
      for (let i = 0; i < filteredProjectKeywords.length; i++) {
        let keywordID = flakeID();
        await API.attributes.create({
          PK: "keyword-" + keywordID,
          category: "keyword",
          name: filteredProjectKeywords[i],
        });
        keywordIDs.push(keywordID);
      }
    } catch (err) {
      // console.log(err.message);
    }
  };

  // Handles creating vessels in the Attributes table
  async function handleCreateVessels() {
    // vesselChoices contains all of the keywords from the database and is used to make suggestions to the user
    let vesselChoices = getAttributesByName("vessel");
    vesselChoices = vesselChoices.map(obj => obj.name); // Formats vesselChoices to get rid of object properties and just leave the names of the vessels in the array

    let filteredProjectVessels = projectData.vessels.slice();
    filteredProjectVessels = filteredProjectVessels.filter(item => !vesselChoices.includes(item)); // Checks project vessels and filters out any vessels that already exist in the database

    try {
      for (let i = 0; i < filteredProjectVessels.length; i++) {
        let vesselID = flakeID();
        await API.attributes.create({
          PK: "vessel-" + vesselID,
          category: "vessel",
          name: filteredProjectVessels[i],
        });
        vesselIDs.push(vesselID);
      }
    } catch (err) {
      // console.log(err.message);
    }
  };

  function handleCreateFundingReference() {
    let references = projectData.sponsorList.slice(1);
    let arr = [
      {
        title: projectData.awardTitle !== "" ? projectData.awardTitle : "null",
        name: projectData.sponsor !== "" ? projectData.sponsor : "null",
        number: projectData.grantNo !== "" ? projectData.grantNo : "null",
      },
    ];
    for (let i = 0; i < references.length; i++) {
      let params = {
        title: references[i].title,
        name: references[i].name,
        number: references[i].number,
      };
      arr.push(params);
    }

    return arr;
  };
  // PROJECT UPLOAD END #############################################################################################################################################

  //Method which allow users to proceed to next page
  function nextPage() {
    const page = step;
    setStep(page + 1);
    window.scrollTo(0, 0);
  };

  // Method which allow users to return to previous page
  function previousPage() {
    const page = step;
    setStep(page - 1);
    window.scrollTo(0, 0);
  };

  function renderStepComponents() {
    switch (step) {
      case 1:
        return <ProjectDescription
          projectData={projectData}
          setProjectData={setProjectData}
          step={step}
          nextPage={nextPage}
        />

      case 2:
        return <ProjectContributorsInformation
          projectData={projectData}
          setProjectData={setProjectData}
          step={step}
          setStep={setStep}
          nextPage={nextPage}
          previousPage={previousPage}
        />

      case 3:
        return <LicensingInformation
          projectData={projectData}
          setProjectData={setProjectData}
          step={step}
          setStep={setStep}
          nextPage={nextPage}
          previousPage={previousPage}
        />

      case 4:
        return <FileAttachment
          projectData={projectData}
          setProjectData={setProjectData}
          step={step}
          setStep={setStep}
          nextPage={nextPage}
          previousPage={previousPage}
        />

      case 5:
        return <SubmitProject
          projectData={projectData}
          setProjectData={setProjectData}
          step={step}
          setStep={setStep}
          nextPage={nextPage}
          previousPage={previousPage}
          createProjects={createProjects}
        />

      case 6:
        return <Acknowledgement
          projectUploadFail={projectUploadFail}
          projectUploadLoading={projectUploadLoading}
          uploadProgress={uploadProgress}
          projectData={projectData}
          setProjectData={setProjectData}
          setStep={setStep}
          emptyProjectObject={emptyProjectObject}
        />

      default:
        return <h1>Something went wrong</h1>
    }
  }

  return (
    <Authorized>
      {renderStepComponents()}
    </Authorized>
  );
}